import { isEqual } from 'radash';
import type { EditableFields } from '@terra/ui-design-system';
import type { PocBlock } from '@terra/shared-schemas-orchestrator';
import type { FindingEditableField } from '../store/findingEditStore';

const SELECT_PENDING_FIELDS = new Set<FindingEditableField>([
  'status',
  'category',
  'severity',
]);

const CVSS_LINKED_FIELDS = [
  'cvss',
  'cvss40',
  'cvss31',
  'severity',
  'severityReason',
] as const satisfies readonly FindingEditableField[];

const POC_BLOCK_TYPES = new Set(['narrative', 'evidence', 'image']);

export type FindingSuggestionChromeKind = 'select' | 'text';

export const FINDING_SUGGESTION_CHROME_KIND = {
  select: 'select',
  text: 'text',
} as const satisfies Record<string, FindingSuggestionChromeKind>;

export function getFindingSuggestionChromeKind(
  field: FindingEditableField,
): FindingSuggestionChromeKind {
  return SELECT_PENDING_FIELDS.has(field)
    ? FINDING_SUGGESTION_CHROME_KIND.select
    : FINDING_SUGGESTION_CHROME_KIND.text;
}

export function getLinkedFindingSuggestionFields({
  field,
  pendingFields,
}: {
  field: FindingEditableField;
  pendingFields: readonly FindingEditableField[];
}): FindingEditableField[] {
  const pendingSet = new Set(pendingFields);

  if (
    CVSS_LINKED_FIELDS.some((linkedField) => linkedField === field) &&
    CVSS_LINKED_FIELDS.some((linkedField) => pendingSet.has(linkedField))
  ) {
    return CVSS_LINKED_FIELDS.filter((linkedField) =>
      pendingSet.has(linkedField),
    );
  }

  return pendingSet.has(field) ? [field] : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPocBlockValue(value: unknown): value is PocBlock {
  if (!isRecord(value) || typeof value.type !== 'string') {
    return false;
  }

  return POC_BLOCK_TYPES.has(value.type);
}

function isPocBlockArray(value: unknown): value is PocBlock[] {
  return Array.isArray(value) && value.every(isPocBlockValue);
}

export type PocBlocksSuggestionLabels = {
  image: string;
  whyItMatters: string;
  request: string;
  response: string;
};

function formatPocBlockSuggestionText({
  block,
  labels,
}: {
  block: PocBlock;
  labels: PocBlocksSuggestionLabels;
}): string {
  if (block.type === 'narrative') {
    return block.content;
  }

  if (block.type === 'image') {
    return block.caption
      ? `${labels.image}: ${block.fileName}\n${block.caption}`
      : `${labels.image}: ${block.fileName}`;
  }

  const parts = [block.explanation];

  if (block.whyItMatters) {
    parts.push(`${labels.whyItMatters}: ${block.whyItMatters}`);
  }

  if (block.manualRequest) {
    parts.push(`${labels.request}:\n${block.manualRequest}`);
  }

  if (block.manualResponse) {
    parts.push(`${labels.response}:\n${block.manualResponse}`);
  }

  return parts.join('\n');
}

export function formatPocBlocksSuggestionValue({
  value,
  labels,
}: {
  value: unknown;
  labels: PocBlocksSuggestionLabels;
}): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (!isPocBlockArray(value)) {
    return '';
  }

  if (value.length === 0) {
    return '';
  }

  return value
    .map((block) => formatPocBlockSuggestionText({ block, labels }))
    .join('\n\n');
}

export function formatFindingSuggestionValue({
  value,
  labels,
}: {
  value: unknown;
  labels?: PocBlocksSuggestionLabels;
}): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (isPocBlockArray(value)) {
    if (!labels) {
      return '';
    }

    return formatPocBlocksSuggestionValue({ value, labels });
  }

  return JSON.stringify(value);
}

export function isEmptyFindingSuggestionValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

export function areFindingSuggestionValuesEqual({
  left,
  right,
}: {
  left: unknown;
  right: unknown;
}): boolean {
  return isEqual(left, right);
}

export function isFindingSuggestionAtBaseline({
  current,
  before,
  original,
}: {
  current: unknown;
  before: unknown;
  original?: unknown;
}): boolean {
  if (areFindingSuggestionValuesEqual({ left: current, right: before })) {
    return true;
  }

  return (
    isEmptyFindingSuggestionValue(before) &&
    original !== undefined &&
    areFindingSuggestionValuesEqual({ left: current, right: original })
  );
}

export function shouldApplyPendingSuggestionAfter({
  current,
  before,
  after,
  original,
}: {
  current: unknown;
  before: unknown;
  after: unknown;
  original?: unknown;
}): boolean {
  if (areFindingSuggestionValuesEqual({ left: current, right: after })) {
    return false;
  }

  return isFindingSuggestionAtBaseline({ current, before, original });
}

export function resolveAcceptedFindingSuggestionValue({
  current,
  before,
  after,
  original,
}: {
  current: unknown;
  before: unknown;
  after: unknown;
  original?: unknown;
}): unknown {
  if (areFindingSuggestionValuesEqual({ left: current, right: after })) {
    return after;
  }

  if (isFindingSuggestionAtBaseline({ current, before, original })) {
    return after;
  }

  return current;
}

export function resolveDiscardedFindingSuggestionValue({
  before,
  original,
}: {
  before: unknown;
  original?: unknown;
}): unknown {
  if (
    isEmptyFindingSuggestionValue(before) &&
    original !== undefined &&
    !isEmptyFindingSuggestionValue(original)
  ) {
    return original;
  }

  return before;
}

export function shouldShowFindingEditSurface({
  mode,
  hasPendingSuggestions,
  isCopilotProcessing,
}: {
  mode: 'view' | 'edit';
  hasPendingSuggestions: boolean;
  isCopilotProcessing: boolean;
}): boolean {
  return mode === 'edit' || hasPendingSuggestions || isCopilotProcessing;
}

export function shouldPersistFindingEditDraft({
  mode,
  hasPendingSuggestions,
}: {
  mode: 'view' | 'edit';
  hasPendingSuggestions: boolean;
}): boolean {
  return mode === 'edit' || hasPendingSuggestions;
}

export function applyPendingSuggestionsToEditableFields({
  fields,
  pendingFields,
  originalFields,
  holdPendingSuggestions,
}: {
  fields: EditableFields;
  pendingFields: readonly {
    field: FindingEditableField;
    before: unknown;
    after: unknown;
  }[];
  originalFields?: EditableFields | null;
  holdPendingSuggestions: boolean;
}): EditableFields {
  let didChange = false;
  const nextFields = { ...fields };

  for (const entry of pendingFields) {
    const currentValue = nextFields[entry.field];

    if (holdPendingSuggestions) {
      if (
        !areFindingSuggestionValuesEqual({
          left: currentValue,
          right: entry.after,
        })
      ) {
        continue;
      }

      const heldValue = resolveDiscardedFindingSuggestionValue({
        before: entry.before,
        original: originalFields?.[entry.field],
      });

      if (
        !areFindingSuggestionValuesEqual({
          left: currentValue,
          right: heldValue,
        })
      ) {
        Object.assign(nextFields, { [entry.field]: heldValue });
        didChange = true;
      }

      continue;
    }

    if (
      shouldApplyPendingSuggestionAfter({
        current: currentValue,
        before: entry.before,
        after: entry.after,
        original: originalFields?.[entry.field],
      })
    ) {
      Object.assign(nextFields, { [entry.field]: entry.after });
      didChange = true;
    }
  }

  return didChange ? nextFields : fields;
}
